import { z } from 'zod';

// ─── Application language enum & helpers ─────────────────────────────

export enum Language {
  NL = 'NL',
  EN = 'EN',
  DE = 'DE',
  FR = 'FR',
  LT = 'LT',
  PL = 'PL',
  RO = 'RO',
  TR = 'TR',
}

const LANGUAGE_MAP = new Map<string, Language>(Object.values(Language).map((v) => [v, v]));

export function parseLanguage(language: string): Language {
  return LANGUAGE_MAP.get(language.toUpperCase()) ?? Language.EN;
}

export const LanguageEnum = z.enum(['NL', 'EN', 'DE', 'FR', 'LT', 'PL', 'RO', 'TR']);

export const LANGUAGE_VALUES: { key: string; name: string }[] = Object.values(Language).map((v) => ({
  key: v,
  name: v,
}));

export const VALID_LANGUAGES = new Set(Object.values(Language).map((v) => v.toLowerCase()));

// ─── Full language name lookup (ISO 639-1) ───────────────────────────

const LANG_MAP: Record<string, string> = {
  aa: 'Afar',
  ab: 'Abkhazian',
  af: 'Afrikaans',
  am: 'Amharic',
  ar: 'Arabic',
  as: 'Assamese',
  ay: 'Aymara',
  az: 'Azerbaijani',
  ba: 'Bashkir',
  be: 'Belarusian',
  bg: 'Bulgarian',
  bh: 'Bihari',
  bi: 'Bislama',
  bn: 'Bengali',
  bo: 'Tibetan',
  br: 'Breton',
  ca: 'Catalan',
  co: 'Corsican',
  cs: 'Czech',
  cy: 'Welsh',
  da: 'Danish',
  de: 'German',
  dz: 'Dzongkha',
  el: 'Greek',
  en: 'English (UK)',
  'en-us': 'English (US)',
  'en-au': 'English (Australia)',
  eo: 'Esperanto',
  es: 'Spanish',
  et: 'Estonian',
  eu: 'Basque',
  fa: 'Persian',
  fi: 'Finnish',
  fj: 'Fijian',
  fo: 'Faroese',
  fr: 'French (France)',
  'fr-ca': 'French (Canada)',
  fy: 'Western Frisian',
  ga: 'Irish',
  gd: 'Scottish Gaelic',
  gl: 'Galician',
  gn: 'Guarani',
  gu: 'Gujarati',
  ha: 'Hausa',
  he: 'Hebrew',
  hi: 'Hindi',
  hr: 'Croatian',
  hu: 'Hungarian',
  hy: 'Armenian',
  ia: 'Interlingua',
  id: 'Indonesian',
  ie: 'Interlingue',
  ik: 'Inupiaq',
  is: 'Icelandic',
  it: 'Italian',
  iu: 'Inuktitut',
  ja: 'Japanese',
  jv: 'Javanese',
  ka: 'Georgian',
  kk: 'Kazakh',
  kl: 'Kalaallisut',
  km: 'Khmer',
  kn: 'Kannada',
  ko: 'Korean',
  ks: 'Kashmiri',
  ku: 'Kurdish',
  ky: 'Kyrgyz',
  la: 'Latin',
  ln: 'Lingala',
  lo: 'Lao',
  lt: 'Lithuanian',
  lv: 'Latvian',
  mg: 'Malagasy',
  mi: 'Maori',
  mk: 'Macedonian',
  ml: 'Malayalam',
  mn: 'Mongolian',
  mo: 'Moldavian',
  mr: 'Marathi',
  ms: 'Malay',
  mt: 'Maltese',
  my: 'Burmese',
  na: 'Nauru',
  ne: 'Nepali',
  nl: 'Dutch',
  no: 'Norwegian',
  nb: 'Norwegian',
  oc: 'Occitan',
  om: 'Oromo',
  or: 'Oriya',
  pa: 'Punjabi',
  pl: 'Polish',
  ps: 'Pashto',
  pt: 'Portuguese',
  'pt-br': 'Portuguese (Brazil)',
  qu: 'Quechua',
  rm: 'Romansh',
  rn: 'Kirundi',
  ro: 'Romanian',
  ru: 'Russian',
  rw: 'Kinyarwanda',
  sa: 'Sanskrit',
  sd: 'Sindhi',
  sg: 'Sango',
  sh: 'Serbo-Croatian',
  si: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  sm: 'Samoan',
  sn: 'Shona',
  so: 'Somali',
  sq: 'Albanian',
  sr: 'Serbian',
  ss: 'Swati',
  st: 'Southern Sotho',
  su: 'Sundanese',
  sv: 'Swedish',
  sw: 'Swahili',
  ta: 'Tamil',
  te: 'Telugu',
  tg: 'Tajik',
  th: 'Thai',
  ti: 'Tigrinya',
  tk: 'Turkmen',
  tl: 'Tagalog',
  tn: 'Tswana',
  to: 'Tonga',
  tr: 'Turkish',
  ts: 'Tsonga',
  tt: 'Tatar',
  tw: 'Twi',
  ug: 'Uyghur',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  vo: 'Volapük',
  wo: 'Wolof',
  xh: 'Xhosa',
  yi: 'Yiddish',
  yo: 'Yoruba',
  za: 'Zhuang',
  zh: 'Chinese',
  zu: 'Zulu',
};

/**
 * Resolve a language code to its full human-readable name.
 *
 * Normalizes the input (lowercase, trim, `_` → `-`) and looks up the full
 * code first (e.g. `en-us` → "English (US)"), then falls back to the base
 * 2-letter code (e.g. `en` → "English (UK)").
 * Returns `"English (UK)"` for unrecognized codes.
 */
export function getFullLanguageName(langCode: string): string {
  const normalized = langCode.trim().toLowerCase().replace(/_/g, '-');
  return LANG_MAP[normalized] ?? LANG_MAP[normalized.split('-')[0]] ?? 'English (UK)';
}
